import React, { useContext } from "react";
import { Navigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { AuthContext } from "./authContext";
import routes from "../config/routes";

/**
 * ProtectedRoute — garde les routes selon authentification + rôles + permissions.
 *
 * Props :
 *  - element      : composant page à rendre
 *  - model        : modèle Django requis (ex: "order")
 *  - action       : action requise (read | create | update | delete)
 *  - allowedRoles : liste de rôles autorisés (ex: ["Admin", "Manager"])
 */
const ProtectedRoute = ({
  element: Component,
  model = null,
  action = "read",
  allowedRoles = null,
}) => {
  const { user, loading, isAuthenticated } = useContext(AuthContext);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        aria-label="Vérification de l'authentification"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={routes.LOGIN} replace />;
  }

  // Vérification par rôle
  if (Array.isArray(allowedRoles)) {
    const userRoles = user?.roles || [];
    const hasRole = userRoles.some((role) => allowedRoles.includes(role));

    if (!hasRole) {
      return <AccessDenied reason={`Rôle requis : ${allowedRoles.join(" ou ")}`} />;
    }
  }

  // Vérification par permission
  if (model && action) {
    const permissions = user?.permissions || {};
    const allowedModels = permissions[action] || [];

    if (!allowedModels.includes(model)) {
      return <AccessDenied reason={`Permission requise : ${model}.${action}`} />;
    }
  }

  return <Component />;
};

const AccessDenied = ({ reason }) => (
  <Box
    sx={{ padding: 5, textAlign: "center" }}
    role="alert"
    aria-live="assertive"
  >
    <Typography variant="h5" color="error" gutterBottom>
      Accès refusé
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      {reason}
    </Typography>
    <Button variant="outlined" onClick={() => window.history.back()}>
      Retour
    </Button>
  </Box>
);

export default ProtectedRoute;
